import type { Provider } from '../api';

interface ProviderMarkProps {
  provider: Provider;
  label: string;
}

const providerMarks = {
  openai: <OpenAIMark />,
  anthropic: <AnthropicMark />,
  gemini: <GeminiMark />,
  openrouter: <OpenRouterMark />,
  grok: <GrokMark />,
} satisfies Record<Provider, JSX.Element>;

export function ProviderMark({ provider, label }: ProviderMarkProps) {
  return (
    <span className={`provider-mark provider-${provider}`} aria-label={label} title={label}>
      {providerMarks[provider]}
    </span>
  );
}

function GrokMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 32 32" focusable="false">
      <circle cx="16" cy="16" r="11" />
      <path d="m11 11 10 10M21 11 11 21" />
    </svg>
  );
}

function OpenAIMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 41 41" focusable="false">
      <path d="M37.532 16.87a9.96 9.96 0 0 0-.856-8.184 10.078 10.078 0 0 0-10.855-4.835A9.964 9.964 0 0 0 18.307.5a10.079 10.079 0 0 0-9.614 6.977 9.962 9.962 0 0 0-6.664 4.834 10.078 10.078 0 0 0 1.24 11.817 9.96 9.96 0 0 0 .856 8.184 10.078 10.078 0 0 0 10.855 4.835 9.964 9.964 0 0 0 7.514 3.351 10.078 10.078 0 0 0 9.614-6.977 9.962 9.962 0 0 0 6.664-4.834 10.078 10.078 0 0 0-1.24-11.817Z" />
      <path d="M22.497 37.886a7.379 7.379 0 0 1-4.744-1.722l.234-.133 7.875-4.548a1.296 1.296 0 0 0 .655-1.134V19.25l3.327 1.92a.12.12 0 0 1 .066.092v9.196a7.414 7.414 0 0 1-7.413 7.428Zm-16.103-6.89a7.364 7.364 0 0 1-.883-4.97l.234.14 7.875 4.548a1.296 1.296 0 0 0 1.31 0l9.612-5.55v3.839a.12.12 0 0 1-.048.103l-7.963 4.596a7.414 7.414 0 0 1-10.137-2.706ZM4.297 13.62a7.379 7.379 0 0 1 3.86-3.248v9.359a1.296 1.296 0 0 0 .655 1.134l9.612 5.55-3.327 1.92a.12.12 0 0 1-.114.011l-7.963-4.6A7.414 7.414 0 0 1 4.297 13.62Zm27.89 6.517-9.612-5.55 3.327-1.918a.12.12 0 0 1 .114-.012l7.963 4.596a7.414 7.414 0 0 1-1.117 13.377v-9.36a1.296 1.296 0 0 0-.675-1.133Zm3.302-5.168-.234-.14-7.875-4.548a1.296 1.296 0 0 0-1.31 0l-9.612 5.55v-3.839a.12.12 0 0 1 .048-.103l7.963-4.596a7.414 7.414 0 0 1 11.02 7.676Zm-20.986 6.78-3.327-1.92a.12.12 0 0 1-.066-.092v-9.196a7.414 7.414 0 0 1 12.157-5.704l-.234.133-7.875 4.548a1.296 1.296 0 0 0-.655 1.134v11.097Zm1.955-3.661 4.28-2.471 4.28 2.471v4.942l-4.28 2.471-4.28-2.471v-4.942Z" />
    </svg>
  );
}

function AnthropicMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 32 32" focusable="false">
      <path d="M9.03 25.64 15.99 6.36h4.03l6.95 19.28h-4.1l-1.42-4.2h-6.9l-1.42 4.2h-4.1Zm6.52-7.17h4.9l-2.45-7.25-2.45 7.25ZM5.03 25.64l6.95-19.28h4.05L9.08 25.64H5.03Z" />
    </svg>
  );
}

function GeminiMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 192 192" focusable="false">
      <path d="M164.93 86.68c-13.56-5.84-25.42-13.84-35.6-24.01-10.17-10.17-18.18-22.04-24.01-35.6-2.23-5.19-4.04-10.54-5.42-16.02C99.45 9.26 97.85 8 96 8s-3.45 1.26-3.9 3.05c-1.38 5.48-3.18 10.81-5.42 16.02-5.84 13.56-13.84 25.43-24.01 35.6-10.17 10.16-22.04 18.17-35.6 24.01-5.19 2.23-10.54 4.04-16.02 5.42C9.26 92.55 8 94.15 8 96s1.26 3.45 3.05 3.9c5.48 1.38 10.81 3.18 16.02 5.42 13.56 5.84 25.42 13.84 35.6 24.01 10.17 10.17 18.18 22.04 24.01 35.6 2.24 5.2 4.04 10.54 5.42 16.02A4.03 4.03 0 0 0 96 184c1.85 0 3.45-1.26 3.9-3.05 1.38-5.48 3.18-10.81 5.42-16.02 5.84-13.56 13.84-25.42 24.01-35.6 10.17-10.17 22.04-18.18 35.6-24.01 5.2-2.24 10.54-4.04 16.02-5.42A4.03 4.03 0 0 0 184 96c0-1.85-1.26-3.45-3.05-3.9-5.48-1.38-10.81-3.18-16.02-5.42Z" />
    </svg>
  );
}

function OpenRouterMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 32 32" focusable="false">
      <path d="M5 10h10.8c3.1 0 5.6 2.5 5.6 5.6v.8c0 3.1-2.5 5.6-5.6 5.6H5" />
      <path d="M17.4 6.8 25 14l-7.6 7.2M25 14h3.2" />
    </svg>
  );
}
